import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SectionTitleBarRef } from '@/pages/components/section-title-bar'
import { debounce } from 'lodash'
import { useAppDispatch, useAppSelector } from '@/redux/hooks.ts'
import { selectTheme } from '@/features/setting/setting.slice.ts'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { CloseOutlined } from '@ant-design/icons'
import { FAQ_BASE_URL, isV2Ready } from '@/utils/helpers.ts'
import { IdeaListUserControls } from './components/idea-list-user-controls.tsx'
import {
  IdeaListTable,
  IdeaListTableRef,
} from './components/idea-list-table/idea-list-table.tsx'
import { CreateIdeaFormFieldTypes } from './components/idea-modal/idea-action/action-idea-form.tsx'
import { ActionIdeaModal } from './components/idea-modal/idea-action/action-idea-modal.tsx'
import Alert from 'antd/es/alert/Alert'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Form from 'antd/es/form/index'
import { SummaryInfo } from './components/idea-summary-info/idea-list-summary-info.tsx'
import { statisticsApi } from '@/apis/statistics/statistics-api.api.ts'
import { selectIdeaListInput } from '@/redux/slices/idea-list-input.slice.ts'
import { selectUser } from '@/features/user/user.slice.ts'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { useTranslation } from 'react-i18next'
import {
  IdeaListMobile,
  IdeaListMobileRef,
} from './components/idea-list-mobile/idea-list-mobile.tsx'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import { IdeaListTour } from './components/idea-list-tour.tsx'
import { Theme } from '@/features/setting/setting.def.ts'
import { SHOW_IDEA_LIST_TUTORIAL_BANNER } from '@/utils/constants.ts'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '@/utils/permissions.ts'
import { useLazyClearBookIdeaAlertsQuery } from '@/apis/book/book-api.api.ts'
import { IdeaListUserFilterControlsMobile } from '@/pages/idea-list/components/idea-list-user-filter-controls-mobile.tsx'
import { IdeaListUserActionControls } from '@/pages/idea-list/components/idea-list-user-action-controls.tsx'
import './idea-list.scss'

const MemoizedIdeaListTable = memo(IdeaListTable)
const MemoizedIdeaListMobile = memo(IdeaListMobile)

export const IdeaList = () => {
  const [form] = Form.useForm<CreateIdeaFormFieldTypes>()
  const actionControlsRef = useRef<SectionTitleBarRef>(null)
  const [showActionIdeaModal, setShowActionIdeaModal] = useState<boolean>(false)
  const [showLiquidateIdeaModal, setShowLiquidateIdeaModal] =
    useState<boolean>(false)
  const [actionIdeaId, setActionIdeaId] = useState<string | null>(null)
  const tableRef = useRef<IdeaListTableRef>(null)
  const mobileRef = useRef<IdeaListMobileRef>(null)
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const userInfo = useAppSelector(selectUser)
  const dispatch = useAppDispatch()
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const { t } = useTranslation()
  const [openTour, setOpenTour] = useState<boolean>(false)
  const colorTheme = useAppSelector(selectTheme)
  const isDarkTheme = colorTheme === Theme.Dark
  const [allExpanded, setAllExpanded] = useState<boolean>(false)
  // Switching view remounts or swaps the grid, so the expand state must reset.
  const viewKey = isMobile ? ideaListInput.mobileView : 'desktop'
  const [prevViewKey, setPrevViewKey] = useState(viewKey)
  if (prevViewKey !== viewKey) {
    setPrevViewKey(viewKey)
    setAllExpanded(false)
  }
  const [showTutorialBanner, setShowTutorialBanner] = useState<boolean>(
    () => localStorage.getItem(SHOW_IDEA_LIST_TUTORIAL_BANNER) === 'true'
  )

  const canCreateIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const [ClearBookIdeaAlerts] = useLazyClearBookIdeaAlertsQuery()

  // Tracks the bookId used by the last statistics fetch. When the table refresh
  // is triggered by a filter change that also changed bookId, the statistics
  // queries already refetch via their bookId arg — so we must NOT additionally
  // invalidate the 'statistics' tag, otherwise each of buying-power /
  // net-exposure / fully-pnl fires twice for the same bookId.
  const lastStatisticsBookId = useRef(ideaListInput.bookId)

  // Debounced statistics invalidation. A single user action (e.g. create/update
  // idea) can trigger the table refresh through several paths within a short
  // window (modal close + post-action refetch re-render, desktop + mobile refs).
  // Each path calls handleTableRefreshed, so invalidating synchronously would
  // refetch buying-power / net-exposure / fully-pnl multiple times. Debouncing
  // (trailing) coalesces the burst into a single invalidation using the latest
  // state.
  const invalidateStatistics = useMemo(
    () =>
      debounce(
        () => dispatch(statisticsApi.util.invalidateTags(['statistics'])),
        300
      ),
    [dispatch]
  )

  // Cancel any pending invalidation on unmount to avoid dispatching after the
  // component is gone.
  useEffect(() => () => invalidateStatistics.cancel(), [invalidateStatistics])

  // Record the mobile idea list view actually shown. This lives here (not in
  // IdeaListMobile) because that component only renders for the 'card' view, so
  // tracking inside it would never capture 'table'. Fires once per distinct
  // view while on mobile, so it also captures users who keep the default and
  // never fire IdeaListMobileViewChanged.
  const lastRecordedView = useRef<string | null>(null)
  useEffect(() => {
    if (!isMobile) {
      lastRecordedView.current = null
      return
    }
    if (lastRecordedView.current === ideaListInput.mobileView) return
    lastRecordedView.current = ideaListInput.mobileView
    newRelic.recordCustomEvent(NEW_RELIC.CUSTOM_EVENT.ideaListMobileViewShown, {
      view: ideaListInput.mobileView,
    })
  }, [isMobile, ideaListInput.mobileView])

  // Called after table data refreshes — invalidate statistics cache and update
  // the title bar timestamp.
  // A+C fix: skip the statistics invalidation when this refresh was caused by a
  // bookId change (filter selection). In that case the bookId-keyed queries
  // already refetch on their own; invalidating again would double-fetch.
  const handleTableRefreshed = useCallback(() => {
    const bookIdChanged = lastStatisticsBookId.current !== ideaListInput.bookId
    lastStatisticsBookId.current = ideaListInput.bookId

    if (!bookIdChanged) {
      // Data-only refresh (auto-refresh, manual refresh, CRUD) — bookId is the
      // same, so bookId won't trigger a refetch; invalidate to keep in sync.
      // Debounced so multiple refresh paths from one action fire it only once.
      invalidateStatistics()
    }

    actionControlsRef.current?.refreshTimestamp()
  }, [invalidateStatistics, ideaListInput.bookId])

  const handleCloseActionIdeaModal = () => {
    // Only close and refresh here; the modal clears the form and editId in its
    // afterClose so fields stay visible during the close animation.
    setShowActionIdeaModal(false)
    tableRef.current?.refresh()
    mobileRef.current?.refresh()
  }

  useEffect(() => {
    const handler = () => {
      tableRef.current?.refresh()
      mobileRef.current?.refresh()
    }
    window.addEventListener('refresh-idea-list', handler)
    return () => window.removeEventListener('refresh-idea-list', handler)
  }, [])

  const handleEditIdea = useCallback((id: string) => {
    setActionIdeaId(id)
    setShowActionIdeaModal(true)
  }, [])

  const handleLiquidateIdea = useCallback((id: string) => {
    setActionIdeaId(id)
    setShowActionIdeaModal(true)
    setShowLiquidateIdeaModal(true)
  }, [])

  const handleCloseTutorialBanner = () => {
    setShowTutorialBanner(false)
    localStorage.setItem(SHOW_IDEA_LIST_TUTORIAL_BANNER, 'false')
  }

  const handleClearIdeaAlerts = () => {
    ClearBookIdeaAlerts({ bookId: userInfo.bookId }).then(() => {
      tableRef.current?.refresh()
      mobileRef.current?.refresh()
    })
  }

  const handleReloadList = useCallback(() => {
    tableRef.current?.refresh()
    mobileRef.current?.refresh()
  }, [])

  const handleToggleExpand = () => {
    if (allExpanded) {
      tableRef.current?.collapseAll()
    } else {
      tableRef.current?.expandAll()
    }
    // Absolute value, not a toggle: the grid also reports this via
    // onAllExpandedChange synchronously, and a toggle would flip it back.
    setAllExpanded(!allExpanded)
  }

  return (
    <>
      <div className={'idea-list'}>
        <div>
          <div className={'idea-list__intro'}>
            {showTutorialBanner && !isMobile && (
              <div className={'idea-list__banner'}>
                <div className={'idea-list__banner-content'}>
                  <CloseOutlined
                    onClick={handleCloseTutorialBanner}
                    className={'idea-list__banner-close'}
                  />
                  <Row className={'idea-list__banner-welcome'}>
                    <IdeyaTypography.Text
                      fontWeight={500}
                      className={'idea-list__banner-welcome-text'}
                    >
                      {t('tutorial.idea_list.welcome_to_ideya')}
                    </IdeyaTypography.Text>
                  </Row>
                  <Row className={'idea-list__banner-description'}>
                    <IdeyaTypography.Text>
                      {t('tutorial.idea_list.banner_description')}
                    </IdeyaTypography.Text>
                  </Row>
                  <Row gutter={[8, 0]} align={'bottom'}>
                    <Col>
                      <IdeyaButton
                        size={'small'}
                        className={'idea-list__banner-start-btn'}
                        onClick={() => setOpenTour(true)}
                      >
                        {t('tutorial.start_tutorial')}
                      </IdeyaButton>
                    </Col>
                    <Col>
                      <a
                        href={`${FAQ_BASE_URL}/en`}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        className={'idea-list__banner-link-wrapper'}
                      >
                        <IdeyaTypography.Text
                          className={'idea-list__banner-link'}
                        >
                          {isDarkTheme
                            ? t('tutorial.idea_list.view_more_on_faq_page')
                            : t('tutorial.idea_list.view_faq_page')}
                        </IdeyaTypography.Text>
                      </a>
                    </Col>
                    <Col>
                      <div
                        onClick={() => window.open('/rules', '_blank')}
                        className={'idea-list__banner-link-wrapper'}
                      >
                        <IdeyaTypography.Text
                          className={'idea-list__banner-link'}
                        >
                          {t('tutorial.idea_list.rules')}
                        </IdeyaTypography.Text>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
            <SummaryInfo
              contributor={ideaListInput.contributor}
              bookId={ideaListInput.bookId}
              dateRange={ideaListInput.dateRange}
            />
          </div>
          {isV2Ready && (
            <Alert
              type={'success'}
              closable
              showIcon
              message={'Net Exposure Breach: 12%'}
            />
          )}
          <div className={'idea-list__controls'}>
            <IdeaListUserControls />
            {isMobile && <IdeaListUserFilterControlsMobile />}
          </div>
        </div>
        <div className={'idea-list__body'}>
          <IdeaListUserActionControls
            ref={actionControlsRef}
            isAllExpanded={allExpanded}
            onToggleExpand={handleToggleExpand}
            onCreateIdea={() => setShowActionIdeaModal(true)}
            onClearIdeaAlerts={handleClearIdeaAlerts}
            onReload={handleReloadList}
          />
          {isMobile && ideaListInput.mobileView === 'card' ? (
            <MemoizedIdeaListMobile
              ref={mobileRef}
              onEditIdea={handleEditIdea}
              onRefreshed={handleTableRefreshed}
            />
          ) : (
            <MemoizedIdeaListTable
              ref={tableRef}
              onRefreshed={handleTableRefreshed}
              onEditIdea={handleEditIdea}
              onLiquidateIdea={handleLiquidateIdea}
              onAllExpandedChange={setAllExpanded}
              height={'100%'}
            />
          )}
          {isMobile && canCreateIdea && (
            <div className={'idea-list__fab'}>
              <IdeyaButton
                type={'primary'}
                block
                icon={<span className={'idea-list__fab-icon'}>+</span>}
                onClick={() => {
                  setShowActionIdeaModal(true)
                  newRelic.addPageAction(NEW_RELIC.PAGE_ACTION.clickCreateIdea)
                }}
                className={'idea-list__fab-button remove-compact'}
              >
                {t('common.create_idea')}
              </IdeyaButton>
            </div>
          )}
        </div>
      </div>
      <ActionIdeaModal
        showActionModal={showActionIdeaModal}
        showLiquidateModal={showLiquidateIdeaModal}
        setShowLiquidatedModal={setShowLiquidateIdeaModal}
        form={form}
        handleCloseModal={handleCloseActionIdeaModal}
        editIdeaId={actionIdeaId}
        setEditIdeaId={setActionIdeaId}
      />
      <IdeaListTour openTour={openTour} setOpenTour={setOpenTour} />
    </>
  )
}
